/**
 * A rolling window back from now, picked as Explore picks one on a Live
 * source: the `.date-preset` chips and **More** with the rarer windows
 * (`date-range.ts`). The More chip wears the active window's name when it
 * came from the menu. Picking keeps the menu open, as Explore's does; a click
 * outside or Escape closes it.
 *
 * The value is a number in the host's unit (minutes for the queue cards,
 * hours for "What it did"); a pick sends `window-pick` with it.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';

export interface WindowChip {
  value: number;
  /** On the chip: `15m`, `7d`. */
  label: string;
  /** In words, for the tooltip and the menu: `Last 15 minutes`. */
  title: string;
}

@customElement('argus-window-chips')
export class ArgusWindowChips extends LightElement {
  @property({ attribute: false }) chips: readonly WindowChip[] = [];
  @property({ attribute: false }) more: readonly WindowChip[] = [];
  @property({ type: Number }) value = 0;
  /** For the group's aria-label: what the window is of. */
  @property() label = 'Window';
  @state() private open = false;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.close();
  }

  private pick(value: number): void {
    this.dispatchEvent(new CustomEvent('window-pick', { detail: value, bubbles: true }));
  }

  private toggle(): void {
    if (this.open) return this.close();
    this.open = true;
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey);
  }

  private close = (): void => {
    this.open = false;
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
  };

  private onOutside = (e: Event): void => {
    const wrap = this.querySelector('.preset-more-wrap');
    if (wrap && !e.composedPath().includes(wrap)) this.close();
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.close();
  };

  override render(): TemplateResult {
    const fromMenu = this.more.find((m) => m.value === this.value);
    return html`<div class="date-presets argus-window-chips" role="group" aria-label=${this.label}>
      ${this.chips.map((c) => html`<button type="button" class="date-preset ${this.value === c.value ? 'active' : ''}"
        title=${c.title} aria-pressed=${this.value === c.value ? 'true' : 'false'} @click=${() => this.pick(c.value)}>${c.label}</button>`)}
      ${this.more.length
        ? html`<span class="preset-more-wrap">
            <button type="button" class="date-preset preset-more-btn ${fromMenu ? 'active' : ''} ${this.open ? 'is-open' : ''}"
              aria-haspopup="menu" aria-expanded=${this.open ? 'true' : 'false'} title=${fromMenu ? fromMenu.title : 'More windows'}
              @click=${() => this.toggle()}>
              ${fromMenu?.label ?? 'More'}<span class="preset-more-caret" aria-hidden="true"></span>
            </button>
            ${this.open
              ? html`<div class="preset-more-menu" role="menu">
                  <span class="preset-more-head">Rolling</span>
                  ${this.more.map((m) => html`<button type="button" class="preset-more-item ${this.value === m.value ? 'active' : ''}"
                    role="menuitemradio" aria-checked=${this.value === m.value ? 'true' : 'false'} @click=${() => this.pick(m.value)}>${m.title}</button>`)}
                </div>`
              : nothing}
          </span>`
        : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-window-chips': ArgusWindowChips;
  }
}
